<template>
  <div class="vz-title-details" @click="close">
    <div class="vz-title-details-panel" @click.stop>
      <vz-image :src="cover" class="vz-title-details-cover" />
      <h1 class="vz-title-details-name">
        <span>{{ name }}</span>
      </h1>

      <div class="vz-list-item-fav-wrapper">
        <span class="vz-bt-fav" @click="handleFavClick">
          <i
            class="vz-title-details-fav"
            :class="
              inFavorites ? 'fa-solid fa-bookmark' : 'fa-regular fa-bookmark'
            "
          >
          </i>
        </span>
        <span class="tooltip">
          {{
            inFavorites ? 'Remover da minha lista' : 'Adicionar à minha lista'
          }}
        </span>
      </div>

      <i class="ic-x vz-title-details-x" @click="close">
        <span>Fechar</span>
      </i>

      <tabs-bar
        class="vz-tabs-bar"
        :tabs="tabs"
        :value="currentTab"
        @change="changeTab"
      />

      <div
        class="vz-tab-content-box"
        v-content-height="{
          selector: '.vz-tab-content',
          active: contentTransitioning
        }"
      >
        <transition
          name="vz-t-fade"
          mode="out-in"
          @enter="startContentTransition"
          @after-enter="endContentTransition"
        >
          <keep-alive>
            <video-details-info
              class="vz-tab-content vz-info"
              v-if="currentTab === 'details'"
              :description="description"
              :age-rating="specifics.ageRating"
              :cast="specifics.cast"
              :duration="specifics.duration"
              :year="specifics.year"
              :genres="specifics.genres"
              :is-happening="specifics.isHappening"
              :svod="specifics.svod"
              :tvod="specifics.tvod"
              :completion-criteria="title?.completionCriteria"
              :target-audience="title?.targetAudience"
              :learning-goal="title?.learningGoal"
              :content-type="specifics.contentType"
            >
            </video-details-info>

            <seasons-browser
              class="vz-tab-content vz-seasons"
              v-else-if="currentTab === 'episodes'"
              :seasons="seasons"
              :content-type="specifics.contentType"
              @select="selectTitle"
              @play="playTitle"
              @transition-enter="pulseContentTransition"
            >
            </seasons-browser>

            <related-titles
              class="vz-tab-content vz-related"
              v-else-if="currentTab === 'related'"
              :title-id="id"
              @select="selectTitle"
              @play="playTitle"
              @transition-enter="pulseContentTransition"
            >
            </related-titles>
          </keep-alive>
        </transition>
      </div>

      <play-button
        class="vz-bts"
        :ppc="access.ppc"
        :predecessorName="access?.firstPredecessorName"
        :price="rentPrice"
        :content-type="contentType"
        :is-series="isSeries"
        :status="status"
        :classroom="classroom"
        @play="playThisTitle"
        @account="emit('account')"
        @change-plan="emit('change-plan')"
        @complete-profile="emit('complete-profile')"
        @rent="rentThisTitle"
        @create-account="emit('create-account')"
      >
      </play-button>
    </div>
  </div>
</template>

<script>
import VzImage from 'vsp-web-ui-lib/components/atoms/vz-lazy-image.vue';
import TabsBar from 'vsp-web-ui-lib/components/molecules/vz-tabs-bar.vue';
import PlayButton from '@/components/atoms/title-play-button.vue';
import RelatedTitles from '@/components/molecules/related-titles.vue';
import SeasonsBrowser from '@/components/molecules/seasons-browser.vue';
import VideoDetailsInfo from '@/components/molecules/video-details-info.vue';

export default {
  name: 'TitleDetails',
  emits: [
    'close',
    'play',
    'account',
    'change-plan',
    'rent',
    'create-account',
    'fav-add',
    'fav-remove',
    'title-details',
    'complete-profile'
  ],
  components: {
    VideoDetailsInfo,
    SeasonsBrowser,
    RelatedTitles,
    TabsBar,
    VzImage,
    PlayButton
  },

  directives: {
    contentHeight: {
      inserted(el, binding) {
        const content = el.querySelector(binding.value.selector);
        if (content) {
          el.style.height = `${Math.max(content.scrollHeight, 64)}px`;
        }
      },

      componentUpdated(el, binding) {
        if (binding.value.active) {
          const content = el.querySelector(binding.value.selector);
          if (content) {
            el.style.height = `${Math.max(content.scrollHeight, 64)}px`;
          }
        }
      }
    }
  },

  props: {
    title: {
      type: Object,
      default: () => ({
        info: {},
        status: 'idle'
      })
    }
  },

  data: () => ({
    currentTab: 'details',
    contentTransitioning: false
  }),

  beforeMount() {
    const bodyElement = document.querySelector('body');
    bodyElement.style.overflow = 'hidden';
  },

  beforeDestroy() {
    const bodyElement = document.querySelector('body');
    bodyElement.style.overflow = 'initial';
  },

  computed: {
    id() {
      return this.title.info.id || '';
    },

    cover() {
      return this.title.info?.images?.featured?.landscape || '';
    },

    classroom() {
      return this.title.info?.classroom || {};
    },

    name() {
      return this.title.info.title || '';
    },

    description() {
      return this.title.info.descriptions || '';
    },

    access() {
      return this.title.info.access || { ppc: 0, message: '' };
    },

    rentPrice() {
      return this.title.info?.availability?.rent || 0;
    },

    inFavorites() {
      return !!this.title.info.inFavorites;
    },

    specifics() {
      return this.title.info.specifics || {};
    },

    contentType() {
      return this.title.info.specifics.contentType;
    },

    seasons() {
      if (this.title.info.specifics.contentType === 'MODULE') {
        return [this.title.info];
      }
      return this.specifics.seasons || [];
    },

    status() {
      return this.title.status || 'idle';
    },

    isSeries() {
      return !!this.seasons.length;
    },

    tabs() {
      const ret = [{ label: 'Detalhes', value: 'details', disabled: false }];

      if (
        this.isSeries &&
        this.title.info.specifics.contentType === 'TV_SERIES'
      ) {
        ret.push({ label: 'Episódios', value: 'episodes', disabled: false });
      }

      if (
        this.title.info.specifics.contentType === 'COURSE' ||
        this.title.info.specifics.contentType === 'MODULE'
      ) {
        ret.push({ label: 'Aulas', value: 'episodes', disabled: false });
      }

      ret.push({ label: 'Relacionados', value: 'related', disabled: false });

      return ret;
    }
  },

  methods: {
    emit(eventName) {
      this.$emit(eventName, this.title.info);
    },

    close() {
      const bodyElement = document.querySelector('body');
      bodyElement.style.overflow = 'initial';
      this.emit('close');
    },

    startContentTransition() {
      this.$nextTick(() => {
        this.contentTransitioning = true;
      });
    },

    endContentTransition() {
      this.contentTransitioning = false;
    },

    pulseContentTransition() {
      this.startContentTransition();
      setTimeout(this.endContentTransition, 400);
    },

    handleFavClick() {
      const evtName = this.inFavorites ? 'fav-remove' : 'fav-add';
      this.$emit(evtName, this.title.info);
    },

    changeTab(tabValue) {
      this.currentTab = tabValue;
    },

    selectTitle(titleData) {
      this.$emit('title-details', titleData);
    },

    playTitle(titleData) {
      this.$emit('play', titleData);
    },

    playThisTitle() {
      this.playTitle(this.title.info);
    },

    rentThisTitle() {
      this.$emit('rent', this.title.info, this.title);
    }
  }
};
</script>






































































































































































































































<style theme="light-theater" scoped>
.vz-title-details {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  transform: translate(0, 0);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 64px 0 0;
  z-index: 100;
}

.vz-title-details.v-enter-active,
.vz-title-details.v-leave-active {
  overflow: hidden;
  transition-property: transform;
  transition-duration: 0.4s;
}

.vz-title-details.v-enter,
.vz-title-details.v-enter-from,
.vz-title-details.v-leave-to {
  transform: translate(0, 105%);
}

.vz-title-details.v-enter-to,
.vz-title-details.v-leave,
.vz-title-details.v-leave-from {
  transform: translate(0, 0);
}

.vz-title-details-panel {
  position: relative;
  width: 90%;
  max-width: 536px;
  max-height: 100%;
  border-radius: 16px 16px 0 0;
  background-color: #fefefe;
  box-shadow: 0 -1px 16px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  overflow: hidden;
}

.vz-title-details-cover {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 56.25% 0 0;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
}

.vz-title-details-name {
  position: relative;
  width: 100%;
  padding: 56.25% 0 0;
  color: #1a1a1a;
  font-weight: bold;
  margin: 0;
  font-size: 1em;
  background-image: linear-gradient(
    to top,
    #fefefe 0px,
    rgba(254, 254, 254, 0) 86px,
    rgba(254, 254, 254, 0) 100%
  );
}

.vz-title-details-name > span {
  display: block;
  padding: 0 32px;
  width: 100%;
  text-align: center;
  position: absolute;
  left: 0;
  bottom: 16px;
}

.vz-bt-fav,
.vz-title-details-x {
  position: absolute;
  top: 8px;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
  font-size: 16px;
  padding: 8px;
  background-image: radial-gradient(
    closest-side at center,
    rgba(48, 48, 48, 0.25),
    rgba(48, 48, 48, 0)
  );
  cursor: pointer;
  color: #ffffff;
}

.vz-bt-fav:hover,
.vz-title-details-x:hover {
  color: var(--primary-color-over);
}

.vz-bt-fav:active,
.vz-title-details-x:active {
  color: var(--primary-color-active);
}

.vz-bt-fav {
  left: 8px;
}

.vz-anonymous .vz-bt-fav {
  display: none;
}

.vz-list-item-fav-wrapper {
  top: 0;
  left: 0;
  display: inline-block;
}

.tooltip {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 5px;
  border-radius: 4px;
  position: absolute;
  top: 7%;
  right: 47%;
  transform: translateX(-50%);
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;
  margin-top: 5px;
}

.vz-list-item-fav-wrapper:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.vz-title-details-x {
  right: 8px;
}

.vz-title-details-fav > span,
.vz-title-details-x > span {
  display: none;
}

.vz-tab-content-box {
  min-height: 64px;
  height: auto;
  max-height: 100%;
  transition: height 0.4s;
  overflow: hidden;
}

.vz-tab-content {
  max-height: 100.5%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.03);
  border-top: 1px solid var(--primary-color-active);
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.4);
}

@media (min-width: 1024px) {
  .vz-title-details {
    padding-top: 12px;
    padding-bottom: 12px;
    backdrop-filter: blur(4px);
    background-color: rgba(0, 0, 0, 0.4);
    align-items: center;
  }

  .vz-title-details.v-enter-active,
  .vz-title-details.v-leave-active {
    transition-property: transform, background-color;
    transition-duration: 0.4s, 0.3s;
    backdrop-filter: none;
  }

  .vz-title-details.v-enter-active {
    transition-delay: 0s, 0.4s;
  }

  .vz-title-details.v-leave-active {
    transition-duration: 0.4s, 0s;
    transition-delay: 0s, 0s;
  }

  .vz-title-details.v-enter,
  .vz-title-details.v-enter-from,
  .vz-title-details.v-leave-to {
    background-color: rgba(0, 0, 0, 0);
  }

  .vz-title-details.v-enter-to,
  .vz-title-details.v-leave,
  .vz-title-details.v-leave-from {
    background-color: rgba(0, 0, 0, 0.4);
  }

  .vz-title-details-panel {
    border-radius: 16px;
  }
}

@media only screen and (min-width: 1920px) {
  .vz-title-details-panel {
    border-radius: 16px;
  }
}
</style>
